/**
 * AcceptInvitationConsumePage — token-consume side of the
 * `/accept-invitation` flow.
 *
 * Lifecycle:
 *
 *  1. On mount, POST /auth/invitations/preview to learn org_name,
 *     role.display_name, inviter_email_masked, requires_new_account.
 *     If the preview returns 401 anti-enum (or the token prop is
 *     empty), short-circuit to the unified "invalid invitation"
 *     surface — no further calls.
 *  2. Branch the form on requires_new_account:
 *       true  → "Set a password" form (password + confirm)
 *       false → "Join {org}" confirm button (no fields)
 *  3. On submit, POST /auth/invitations/accept. Map five distinct BE
 *     response shapes (see header of the spec for the full table)
 *     to dedicated surfaces.
 *
 * Anti-enum FE policy: any token-invalid response (preview 401,
 * accept 401 with `invitation_token_invalid`, concurrent-mod) maps
 * to ONE invalid surface so the FE never amplifies the BE leak.
 * Distinct error codes (`invitation_login_required`,
 * `invitation_email_mismatch`, `invitation_password_required`) only
 * surface AFTER the BE has constant-time verified the token —
 * surfacing them on the FE does not introduce a new leak.
 *
 * Tokens-only colors via @sparta/design-tokens; touch targets ≥
 * 44 px; `role="status"` aria-live regions; semantic landmarks per
 * CLAUDE.md Frontend rule §7 (WCAG 2.1 AA).
 */

import * as React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  acceptInvitationBodySchema,
  type PreviewInvitationResponse,
} from '@artemis/validators';
import { Button, Card, TextField } from '@sparta/ui-kit';
import { apiClient } from '../../shared/api-client.js';

// ---------------------------------------------------------------------------
// Form schema — derived from the canonical wire contract
// ---------------------------------------------------------------------------

const newAccountFormSchema = acceptInvitationBodySchema
  .pick({ password: true })
  .required({ password: true })
  .extend({ confirmPassword: z.string() })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type NewAccountFormInput = z.infer<typeof newAccountFormSchema>;

// ---------------------------------------------------------------------------
// Page state machine
// ---------------------------------------------------------------------------

type PageState =
  | { kind: 'loading-preview' }
  | { kind: 'invalid-link' }
  | {
      kind: 'new-account-form';
      preview: PreviewInvitationResponse;
      submit: SubmitState;
    }
  | {
      kind: 'existing-account-confirm';
      preview: PreviewInvitationResponse;
      submit: SubmitState;
    }
  | { kind: 'login-required'; emailMasked: string }
  | { kind: 'email-mismatch'; emailMasked: string }
  | { kind: 'success'; orgName: string; wasNewAccount: boolean };

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'temporary-failure'; message: string };

// ---------------------------------------------------------------------------
// Helpers for safely peeking into JSON-typed error bodies
// ---------------------------------------------------------------------------

function readErrorCode(maybeError: unknown): string | undefined {
  if (typeof maybeError !== 'object' || maybeError === null) return undefined;
  const value = (maybeError as { error?: unknown }).error;
  return typeof value === 'string' ? value : undefined;
}

function readEmailMasked(maybeBody: unknown): string {
  if (typeof maybeBody !== 'object' || maybeBody === null) return '';
  const value = (maybeBody as { email_masked?: unknown }).email_masked;
  return typeof value === 'string' ? value : '';
}

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------

export interface AcceptInvitationConsumePageProps {
  /**
   * Raw invitation token from the `?token=` query param. The page
   * short-circuits to the unified invalid-link surface if the token
   * is empty; otherwise the BE preview endpoint validates it via
   * constant-time Argon2id verify.
   */
  token: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AcceptInvitationConsumePage({
  token,
}: AcceptInvitationConsumePageProps): React.ReactElement {
  const [state, setState] = React.useState<PageState>(() =>
    token.length > 0
      ? { kind: 'loading-preview' }
      : { kind: 'invalid-link' },
  );

  // ------------------------------------------------------------------ form
  // The form hook is created unconditionally (React rules); the form is
  // rendered only when the state branch demands it.
  const form = useForm<NewAccountFormInput>({
    resolver: zodResolver(newAccountFormSchema),
    mode: 'onTouched',
    defaultValues: { password: '', confirmPassword: '' },
  });

  // ------------------------------------------------------------ preview call
  React.useEffect(() => {
    if (token.length === 0) {
      return;
    }
    let cancelled = false;

    void (async (): Promise<void> => {
      try {
        const result = await apiClient.POST('/auth/invitations/preview', {
          body: { token },
        });
        if (cancelled) return;
        const { response } = result;
        if (!response.ok) {
          // ALL preview failures collapse to invalid-link — the BE
          // is itself anti-enum here.
          setState({ kind: 'invalid-link' });
          return;
        }
        const data = (result as { data?: unknown }).data;
        // Soft-validate: if the shape is unexpectedly off (BE drift),
        // treat as invalid so the FE never renders an undefined org
        // name. apiClient already reports drift to Sentry.
        if (
          typeof data !== 'object' ||
          data === null ||
          typeof (data as { org_name?: unknown }).org_name !== 'string' ||
          typeof (data as { requires_new_account?: unknown })
            .requires_new_account !== 'boolean'
        ) {
          setState({ kind: 'invalid-link' });
          return;
        }
        const preview = data as PreviewInvitationResponse;
        setState(
          preview.requires_new_account
            ? {
                kind: 'new-account-form',
                preview,
                submit: { kind: 'idle' },
              }
            : {
                kind: 'existing-account-confirm',
                preview,
                submit: { kind: 'idle' },
              },
        );
      } catch {
        if (cancelled) return;
        // Network or unhandled error during preview — fall back to
        // the unified invalid-link surface. Telling the user about
        // an infra failure here would leak nothing useful and
        // confuses recovery (the action is the same: ask the admin
        // to re-send the invite).
        setState({ kind: 'invalid-link' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  // ----------------------------------------------------------- accept caller
  const performAccept = React.useCallback(
    async (currentState: PageState, password?: string): Promise<void> => {
      if (
        currentState.kind !== 'new-account-form' &&
        currentState.kind !== 'existing-account-confirm'
      ) {
        return;
      }
      const preview = currentState.preview;

      setState({ ...currentState, submit: { kind: 'submitting' } });

      try {
        const body: { token: string; password?: string } = { token };
        if (password !== undefined) body.password = password;
        const result = await apiClient.POST('/auth/invitations/accept', {
          body,
        });
        const { response } = result;
        if (response.ok) {
          const data = (result as { data?: unknown }).data;
          const wasNewAccount =
            typeof data === 'object' &&
            data !== null &&
            typeof (data as { was_new_account?: unknown }).was_new_account ===
              'boolean'
              ? ((data as { was_new_account: boolean }).was_new_account)
              : false;
          setState({
            kind: 'success',
            orgName: preview.org_name,
            wasNewAccount,
          });
          return;
        }

        const errorBody = (result as { error?: unknown }).error;
        const code = readErrorCode(errorBody);

        if (response.status === 401 && code === 'invitation_login_required') {
          setState({
            kind: 'login-required',
            emailMasked: readEmailMasked(errorBody),
          });
          return;
        }
        if (response.status === 403 && code === 'invitation_email_mismatch') {
          setState({
            kind: 'email-mismatch',
            emailMasked: readEmailMasked(errorBody),
          });
          return;
        }
        if (
          response.status === 422 &&
          code === 'invitation_password_required'
        ) {
          // BE disagreed with the preview — upgrade the confirm
          // variant into the new-account form so the user can
          // complete the flow without reopening the email link.
          setState({
            kind: 'new-account-form',
            preview,
            submit: { kind: 'idle' },
          });
          return;
        }
        if (response.status === 401) {
          // Anti-enum 401 (including concurrent-mod race) — collapse.
          setState({ kind: 'invalid-link' });
          return;
        }
        if (response.status === 429) {
          setState({
            ...currentState,
            submit: {
              kind: 'temporary-failure',
              message:
                'Too many attempts. Please wait a moment and try again.',
            },
          });
          return;
        }
        if (response.status >= 500) {
          setState({
            ...currentState,
            submit: {
              kind: 'temporary-failure',
              message:
                'Acceptance is temporarily unavailable. Try again shortly.',
            },
          });
          return;
        }
        setState({
          ...currentState,
          submit: {
            kind: 'temporary-failure',
            message: 'Something went wrong. Try again.',
          },
        });
      } catch {
        setState({
          ...currentState,
          submit: {
            kind: 'temporary-failure',
            message: 'Network error. Check your connection and try again.',
          },
        });
      }
    },
    [token],
  );

  // ---------------------------------------------------------- form handlers
  const onSubmitNewAccount = form.handleSubmit(async (values) => {
    await performAccept(state, values.password);
  });

  const onConfirmExisting = async (): Promise<void> => {
    await performAccept(state);
  };

  // ------------------------------------------------------------- shell
  return (
    <main
      className="min-h-screen w-full"
      style={{ background: 'var(--bg-canvas)', color: 'var(--fg-primary)' }}
    >
      <div className="mx-auto max-w-md px-4 py-12 sm:py-16">
        {state.kind === 'loading-preview' ? (
          <LoadingPreviewSurface />
        ) : state.kind === 'invalid-link' ? (
          <InvalidLinkSurface />
        ) : state.kind === 'login-required' ? (
          <LoginRequiredSurface emailMasked={state.emailMasked} />
        ) : state.kind === 'email-mismatch' ? (
          <EmailMismatchSurface emailMasked={state.emailMasked} />
        ) : state.kind === 'success' ? (
          <SuccessSurface
            orgName={state.orgName}
            wasNewAccount={state.wasNewAccount}
          />
        ) : state.kind === 'new-account-form' ? (
          <NewAccountFormSurface
            preview={state.preview}
            submit={state.submit}
            form={form}
            onSubmit={onSubmitNewAccount}
          />
        ) : (
          <ExistingAccountConfirmSurface
            preview={state.preview}
            submit={state.submit}
            onConfirm={onConfirmExisting}
          />
        )}
      </div>
    </main>
  );
}

// ===========================================================================
// Surfaces
// ===========================================================================

function LoadingPreviewSurface(): React.ReactElement {
  return (
    <section role="status" aria-live="polite">
      <p
        className="text-sm"
        style={{ color: 'var(--fg-tertiary)' }}
      >
        Checking invitation…
      </p>
    </section>
  );
}

function InvalidLinkSurface(): React.ReactElement {
  return (
    <Card asChild variant="surface" padding="md">
      <section role="status" aria-live="polite">
        <h1
          className="text-3xl font-bold"
          style={{ letterSpacing: '-0.6px' }}
        >
          This invitation link is not valid
        </h1>
        <p
          className="mt-3 text-sm"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          This invitation can no longer be used. Ask your administrator
          to send a new invitation.
        </p>
        <a
          href="/login"
          className="mt-5 inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
          style={{ color: 'var(--fg-brand)' }}
        >
          ← Back to sign in
        </a>
      </section>
    </Card>
  );
}

function LoginRequiredSurface(props: {
  emailMasked: string;
}): React.ReactElement {
  return (
    <Card asChild variant="surface" padding="md">
      <section role="status" aria-live="polite">
        <h1
          className="text-3xl font-bold"
          style={{ letterSpacing: '-0.6px' }}
        >
          Sign in to accept this invitation
        </h1>
        <p
          className="mt-3 text-sm"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          This invitation is tied to an existing Artemis account
          ({props.emailMasked}). Sign in to that account, then reopen
          the invitation link from your email to accept.
        </p>
        <a
          href="/login"
          className="mt-5 inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
          style={{ color: 'var(--fg-brand)' }}
        >
          Sign in →
        </a>
      </section>
    </Card>
  );
}

function EmailMismatchSurface(props: {
  emailMasked: string;
}): React.ReactElement {
  return (
    <Card asChild variant="surface" padding="md">
      <section role="status" aria-live="polite">
        <h1
          className="text-3xl font-bold"
          style={{ letterSpacing: '-0.6px' }}
        >
          You're signed in as the wrong account
        </h1>
        <p
          className="mt-3 text-sm"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          This invitation is for {props.emailMasked}, but you're signed
          in as a different user. Sign out, sign in as
          {' '}{props.emailMasked}, then reopen the invitation link from
          your email.
        </p>
        <a
          href="/login"
          className="mt-5 inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
          style={{ color: 'var(--fg-brand)' }}
        >
          Go to sign in →
        </a>
      </section>
    </Card>
  );
}

function SuccessSurface(props: {
  orgName: string;
  wasNewAccount: boolean;
}): React.ReactElement {
  return (
    <Card asChild variant="surface" padding="md">
      <section role="status" aria-live="polite">
        <h1
          className="text-3xl font-bold"
          style={{ letterSpacing: '-0.6px' }}
        >
          You're in
        </h1>
        <p
          className="mt-3 text-sm"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          {props.wasNewAccount
            ? `Your account is created and you're now a member of ${props.orgName}.`
            : `Welcome to ${props.orgName}. You're signed in.`}
        </p>
        <a
          href="/"
          className="mt-5 inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
          style={{ color: 'var(--fg-brand)' }}
        >
          Continue to dashboard →
        </a>
      </section>
    </Card>
  );
}

function NewAccountFormSurface(props: {
  preview: PreviewInvitationResponse;
  submit: SubmitState;
  form: ReturnType<typeof useForm<NewAccountFormInput>>;
  onSubmit: (event?: React.BaseSyntheticEvent) => Promise<void>;
}): React.ReactElement {
  const { preview, submit, form, onSubmit } = props;
  const submitting = submit.kind === 'submitting';
  const errors = form.formState.errors;

  const submitErrorMessage: string | undefined =
    submit.kind === 'temporary-failure' ? submit.message : undefined;

  return (
    <>
      <InvitationHeader preview={preview} />

      <form onSubmit={onSubmit} noValidate aria-busy={submitting}>
        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          spellCheck={false}
          autoCapitalize="off"
          disabled={submitting}
          hint="At least 12 characters."
          error={errors.password?.message}
          {...form.register('password')}
        />

        <TextField
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          spellCheck={false}
          autoCapitalize="off"
          disabled={submitting}
          error={errors.confirmPassword?.message}
          {...form.register('confirmPassword')}
        />

        <div
          role="alert"
          aria-live="polite"
          className="mb-3 min-h-5"
        >
          {submitErrorMessage !== undefined ? (
            <p
              className="text-sm"
              style={{ color: 'var(--fg-danger)' }}
            >
              {submitErrorMessage}
            </p>
          ) : null}
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          full
          disabled={submitting}
          aria-busy={submitting}
          aria-disabled={submitting}
        >
          {submitting ? 'Creating account…' : 'Create account & join'}
        </Button>
      </form>
    </>
  );
}

function ExistingAccountConfirmSurface(props: {
  preview: PreviewInvitationResponse;
  submit: SubmitState;
  onConfirm: () => Promise<void>;
}): React.ReactElement {
  const { preview, submit, onConfirm } = props;
  const submitting = submit.kind === 'submitting';
  const submitErrorMessage: string | undefined =
    submit.kind === 'temporary-failure' ? submit.message : undefined;

  return (
    <>
      <InvitationHeader preview={preview} />

      <p
        className="mb-6 text-sm"
        style={{ color: 'var(--fg-tertiary)' }}
      >
        You already have an Artemis account. Click below to join
        {' '}{preview.org_name}.
      </p>

      <div
        role="alert"
        aria-live="polite"
        className="mb-3 min-h-5"
      >
        {submitErrorMessage !== undefined ? (
          <p
            className="text-sm"
            style={{ color: 'var(--fg-danger)' }}
          >
            {submitErrorMessage}
          </p>
        ) : null}
      </div>

      <Button
        type="button"
        variant="primary"
        size="lg"
        full
        disabled={submitting}
        aria-busy={submitting}
        aria-disabled={submitting}
        onClick={() => {
          void onConfirm();
        }}
      >
        {submitting ? 'Joining…' : `Join ${preview.org_name}`}
      </Button>
    </>
  );
}

function InvitationHeader(props: {
  preview: PreviewInvitationResponse;
}): React.ReactElement {
  const { preview } = props;
  return (
    <>
      <h1
        className="text-3xl font-bold"
        style={{ letterSpacing: '-0.6px' }}
      >
        You're invited to {preview.org_name}
      </h1>
      <p
        className="mb-8 mt-2 text-sm"
        style={{ color: 'var(--fg-tertiary)' }}
      >
        Role: <strong>{preview.role.display_name}</strong>. Invited by{' '}
        {preview.inviter_email_masked}.
      </p>
    </>
  );
}
